import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import PathLockSelector from '../components/PathLockSelector';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Target, TrendingUp, GraduationCap, Briefcase, Shield, Rocket, 
  BookOpen, Users, CheckCircle, AlertTriangle, Info, Lock, Unlock,
  ThumbsUp, ThumbsDown, HelpCircle, Undo2, Scale
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion } from 'framer-motion';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MILESTONES = [
  { id: 'exploration', label: 'Exploration', icon: Target },
  { id: 'shortlist', label: 'Shortlist', icon: Users },
  { id: 'lock', label: 'Lock', icon: Lock },
  { id: 'apply', label: 'Apply', icon: CheckCircle },
  { id: 'accepted', label: 'Accepted', icon: ThumbsUp },
  { id: 'commit', label: 'Commit', icon: Lock },
  { id: 'enroll', label: 'Enroll', icon: GraduationCap }
];

const PATH_ICONS = {
  college: GraduationCap, trade: Briefcase, military: Shield,
  entrepreneurship: Rocket, workforce: Users
};

// Confirmation Dialog
const ConfirmDialog = ({ open, title, message, confirmLabel, onConfirm, onCancel, variant = 'default' }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-[#0a1729]/60 z-50 flex items-center justify-center p-4" data-testid="confirm-dialog">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md w-full shadow-2xl">
        <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
        <p className="text-slate-300 text-sm mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel} className="border-slate-600 text-slate-300" data-testid="confirm-cancel-btn">Cancel</Button>
          <Button onClick={onConfirm} className={variant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} data-testid="confirm-ok-btn">{confirmLabel}</Button>
        </div>
      </motion.div>
    </div>
  );
};

export default function MyPathsPage() {
  const { user, getAuthHeader } = useAuth();
  const [paths, setPaths] = useState([]);
  const [milestones, setMilestones] = useState({});
  const [agreements, setAgreements] = useState({});
  const [workflowLoading, setWorkflowLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ open: false });

  const isParent = user?.role === 'parent';
  const studentId = isParent ? user?.linked_children?.[0]?.child_id : user?.id;
  const studentName = isParent ? (user?.linked_children?.[0]?.name || user?.linked_children?.[0]?.child_name || 'Your Child') : user?.full_name;
  
  // Determine user type
  const isCollegeStudent = user?.education_level === 'some_college' || 
                          user?.education_level === 'bachelor' || 
                          user?.education_level === 'graduate' ||
                          (user?.age && user.age >= 18 && user?.role === 'student');
  const isCareerProfessional = user?.role === 'career_professional' || 
                               user?.role === 'career_changer' || 
                               user?.role === 'professional';
  const isHighSchoolStudent = user?.role === 'student' && !isCollegeStudent;

  // Get appropriate title and description
  const getPageInfo = () => {
    if (isCareerProfessional) {
      return {
        title: "My Career Paths",
        description: "Track and lock in your career transition goals. Move from exploring to committed as you finalize your next career move."
      };
    }
    if (isCollegeStudent) {
      return {
        title: "My Future Paths",
        description: "Lock in your post-graduation plans. Whether it's graduate school, career, or entrepreneurship - track your commitment level."
      };
    }
    return {
      title: "My Career Paths",
      description: "Select and lock your top 3 path choices. As you become more certain, progress from Exploring to Finalized."
    };
  };

  const pageInfo = getPageInfo();

  // Fetch decision workflow data
  useEffect(() => {
    if (studentId) fetchWorkflowData();
  }, [studentId]);

  const fetchWorkflowData = async () => {
    setWorkflowLoading(true);
    try {
      const pathsEndpoint = isParent && studentId
        ? `${API}/paths/child/${studentId}`
        : `${API}/paths/my-paths`;
      const [pathsRes, milestonesRes] = await Promise.all([
        axios.get(pathsEndpoint, { ...getAuthHeader(), withCredentials: true }),
        axios.get(`${API}/decisions/milestones/${studentId}`, { ...getAuthHeader(), withCredentials: true })
      ]);
      setPaths(pathsRes.data.paths || []);
      const mMap = {}, aMap = {};
      for (const m of milestonesRes.data.milestones || []) mMap[m.path_id] = m;
      for (const path of pathsRes.data.paths || []) {
        try {
          const aRes = await axios.get(`${API}/decisions/agreements/${path.id}`, { ...getAuthHeader(), withCredentials: true });
          if (aRes.data.agreement) aMap[path.id] = aRes.data.agreement;
        } catch (e) {}
      }
      setMilestones(mMap);
      setAgreements(aMap);
    } catch (e) { console.error('Failed to fetch workflow data:', e); }
    finally { setWorkflowLoading(false); }
  };

  const withConfirm = (title, message, confirmLabel, onConfirm, variant) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, variant, onConfirm: () => { setConfirmDialog({ open: false }); onConfirm(); } });
  };

  const advanceMilestone = async (pathId, milestone) => {
    withConfirm(`Advance to "${MILESTONES.find(m=>m.id===milestone)?.label}"?`, 'You can undo this later.', 'Advance', async () => {
      try {
        await axios.post(`${API}/decisions/milestones/${pathId}/advance?milestone=${milestone}`, {}, { ...getAuthHeader(), withCredentials: true });
        toast.success(`Advanced to ${milestone}`, { action: { label: 'Undo', onClick: () => revertMilestone(pathId) } });
        fetchWorkflowData();
      } catch (e) { toast.error('Failed to advance'); }
    });
  };

  const revertMilestone = async (pathId) => {
    try {
      await axios.post(`${API}/decisions/milestones/${pathId}/revert`, {}, { ...getAuthHeader(), withCredentials: true });
      toast.success('Reverted to previous stage');
      fetchWorkflowData();
    } catch (e) { showFriendlyError(e, { fallback: 'Failed to revert' }); }
  };

  const confirmPath = async (pathId) => {
    withConfirm('Confirm this path?', 'Your family will be notified. You can undo this later.', 'Confirm', async () => {
      try {
        await axios.post(`${API}/decisions/milestones/${pathId}/advance?milestone=shortlist`, {}, { ...getAuthHeader(), withCredentials: true });
        toast.success('Path confirmed!', { action: { label: 'Undo', onClick: () => undoConfirmation(pathId) } });
        fetchWorkflowData();
      } catch (e) { toast.error('Failed to confirm path'); }
    });
  };

  const undoConfirmation = async (pathId) => {
    try {
      await axios.post(`${API}/decisions/agreements/${pathId}/undo-confirmation`, {}, { ...getAuthHeader(), withCredentials: true });
      toast.success('Confirmation undone');
      fetchWorkflowData();
    } catch (e) { toast.error('Failed to undo'); }
  };

  const parentResponse = async (pathId, stance) => {
    const labels = { support: 'Support', consider: 'Consider', not_approved: 'Not Approve' };
    withConfirm(`${labels[stance]} this path?`, 'You can change your response later.', labels[stance], async () => {
      try {
        await axios.post(`${API}/decisions/agreements/${pathId}/parent-response`, { stance, notes: '' }, { ...getAuthHeader(), withCredentials: true });
        toast.success('Response submitted', { action: { label: 'Undo', onClick: () => undoParentResponse(pathId) } });
        fetchWorkflowData();
      } catch (e) { toast.error('Failed to submit response'); }
    }, stance === 'not_approved' ? 'danger' : 'default');
  };

  const undoParentResponse = async (pathId) => {
    try {
      await axios.post(`${API}/decisions/agreements/${pathId}/undo-parent-response`, {}, { ...getAuthHeader(), withCredentials: true });
      toast.success('Response withdrawn');
      fetchWorkflowData();
    } catch (e) { toast.error('Failed to withdraw'); }
  };

  return (
    <AppLayout>
      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false })}
      />

      <PageHeader 
        title={isParent ? `${studentName}'s Paths` : pageInfo.title}
        description={isParent ? 'Review your child\'s path selections and provide feedback' : pageInfo.description}
      />

      {/* How Path Locking Works - Top Banner */}
      <GlassCard className="mb-6 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/30">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
            <Info className="w-6 h-6 text-blue-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-semibold text-lg mb-2">How Path Locking Works</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-slate-500"></div>
                <div>
                  <span className="text-white font-medium text-sm">Exploring</span>
                  <p className="text-slate-300 text-xs">Still researching options</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div>
                  <span className="text-white font-medium text-sm">Soft Lock</span>
                  <p className="text-slate-300 text-xs">Seriously considering, notifies family</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <div>
                  <span className="text-white font-medium text-sm">Hard Lock</span>
                  <p className="text-slate-300 text-xs">Committed, triggers financial planning</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <div>
                  <span className="text-white font-medium text-sm">Finalized</span>
                  <p className="text-slate-300 text-xs">Enrolled/accepted, locked in</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Main Path Lock Selector */}
      <PathLockSelector />

      {/* Additional Context for Different User Types */}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {isHighSchoolStudent && (
          <>
            <GlassCard className="border-purple-500/30">
              <div className="flex items-start gap-3">
                <GraduationCap className="w-6 h-6 text-purple-400 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-semibold mb-1">College Path</h4>
                  <p className="text-slate-300 text-sm">
                    When you soft-lock a college, we'll start building your personalized application timeline and scholarship matches.
                  </p>
                </div>
              </div>
            </GlassCard>
            <GlassCard className="border-amber-500/30">
              <div className="flex items-start gap-3">
                <Briefcase className="w-6 h-6 text-amber-400 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-semibold mb-1">Trade & Workforce</h4>
                  <p className="text-slate-300 text-sm">
                    Lock in a trade or direct workforce path to receive apprenticeship matches and certification roadmaps.
                  </p>
                </div>
              </div>
            </GlassCard>
            <GlassCard className="border-green-500/30">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-green-400 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-semibold mb-1">Military Service</h4>
                  <p className="text-slate-300 text-sm">
                    Lock in a military path to get MOS alignment recommendations and GI Bill planning tools.
                  </p>
                </div>
              </div>
            </GlassCard>
            <GlassCard className="border-pink-500/30">
              <div className="flex items-start gap-3">
                <Rocket className="w-6 h-6 text-pink-400 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-semibold mb-1">Entrepreneurship</h4>
                  <p className="text-slate-300 text-sm">
                    Thinking of starting your own business? Lock in this path to access runway planning tools.
                  </p>
                </div>
              </div>
            </GlassCard>
          </>
        )}

        {isCollegeStudent && (
          <>
            <GlassCard className="border-purple-500/30">
              <div className="flex items-start gap-3">
                <BookOpen className="w-6 h-6 text-purple-400 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-semibold mb-1">Graduate School</h4>
                  <p className="text-slate-300 text-sm">
                    Lock in a graduate program to start planning your application timeline and funding strategy.
                  </p>
                </div>
              </div>
            </GlassCard>
            <GlassCard className="border-blue-500/30">
              <div className="flex items-start gap-3">
                <Briefcase className="w-6 h-6 text-blue-400 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-semibold mb-1">Career Entry</h4>
                  <p className="text-slate-300 text-sm">
                    Lock in your target industry or company to receive tailored job preparation and networking tips.
                  </p>
                </div>
              </div>
            </GlassCard>
          </>
        )}

        {isCareerProfessional && (
          <>
            <GlassCard className="border-cyan-500/30">
              <div className="flex items-start gap-3">
                <TrendingUp className="w-6 h-6 text-cyan-400 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-semibold mb-1">Career Transition</h4>
                  <p className="text-slate-300 text-sm">
                    Lock in your target role or industry to get a personalized skills gap analysis and upskilling roadmap.
                  </p>
                </div>
              </div>
            </GlassCard>
            <GlassCard className="border-emerald-500/30">
              <div className="flex items-start gap-3">
                <Rocket className="w-6 h-6 text-emerald-400 flex-shrink-0" />
                <div>
                  <h4 className="text-white font-semibold mb-1">Entrepreneurship</h4>
                  <p className="text-slate-300 text-sm">
                    Planning to start your own venture? Lock in this path to access financial runway planning and market analysis tools.
                  </p>
                </div>
              </div>
            </GlassCard>
          </>
        )}
      </div>

      {/* What Happens When You Lock */}
      <GlassCard className="mt-6">
        <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-400" />
          What Happens When You Lock a Path
        </h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-slate-800/50">
            <div className="text-yellow-400 font-medium mb-2">Soft Lock</div>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>• Parents/family are notified</li>
              <li>• Preliminary cost estimates generated</li>
              <li>• Related deadlines added to calendar</li>
            </ul>
          </div>
          <div className="p-4 rounded-lg bg-slate-800/50">
            <div className="text-blue-400 font-medium mb-2">Hard Lock</div>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>• Full financial planning activated</li>
              <li>• Application checklist generated</li>
              <li>• Milestone reminders scheduled</li>
            </ul>
          </div>
          <div className="p-4 rounded-lg bg-slate-800/50">
            <div className="text-emerald-400 font-medium mb-2">Finalized</div>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>• Enrollment confirmed in system</li>
              <li>• Post-enrollment planning begins</li>
              <li>• Graduation countdown starts</li>
            </ul>
          </div>
        </div>
      </GlassCard>

      {/* =================== FAMILY DECISION WORKFLOW =================== */}
      <div className="mt-10 border-t border-slate-700 pt-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <Users className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white" data-testid="workflow-section-title">Family Decision Workflow</h2>
            <p className="text-slate-300 text-sm">
              {isParent ? `Review ${studentName}'s choices and provide your input` : 'Track progress and get family approval on your path choices'}
            </p>
          </div>
        </div>

        {workflowLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : paths.length > 0 ? (
          <div className="space-y-6">
            {paths.map(path => {
              const milestone = milestones[path.id];
              const agreement = agreements[path.id];
              const currentMilestone = milestone?.current_milestone || 'exploration';
              const currentIdx = MILESTONES.findIndex(m => m.id === currentMilestone);
              const PathIcon = PATH_ICONS[path.path_type] || Target;
              const statusColors = {
                pending: 'bg-slate-500/20 text-slate-300',
                student_confirmed: 'bg-blue-500/20 text-blue-400',
                parent_support: 'bg-emerald-500/20 text-emerald-400',
                parent_consider: 'bg-amber-500/20 text-amber-400',
                parent_not_approved: 'bg-red-500/20 text-red-400',
                agreed: 'bg-emerald-500/20 text-emerald-400',
              };

              return (
                <GlassCard key={path.id} className="p-6" data-testid={`workflow-card-${path.id}`}>
                  {/* Path Header */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                        <PathIcon className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{path.institution_name}</h3>
                        <p className="text-slate-300 text-sm capitalize">{path.path_type} {path.major_or_track ? `\u2022 ${path.major_or_track}` : ''}</p>
                      </div>
                    </div>
                    {agreement && (
                      <Badge className={`${statusColors[agreement.status] || statusColors.pending} border-none`}>
                        {agreement.status?.replace(/_/g, ' ') || 'Pending'}
                      </Badge>
                    )}
                  </div>

                  {/* Milestone Progress */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-slate-300 text-sm font-medium">Decision Progress</p>
                      {currentIdx > 0 && (
                        <Button variant="outline" size="sm" onClick={() => withConfirm('Undo last step?', 'Revert to previous stage.', 'Undo', () => revertMilestone(path.id), 'danger')}
                          className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 h-7 text-xs" data-testid="revert-btn">
                          <Undo2 className="w-3 h-3 mr-1" /> Undo Step
                        </Button>
                      )}
                    </div>
                    <div className="relative">
                      <div className="absolute top-5 left-0 right-0 h-1 bg-slate-700">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500"
                          style={{ width: `${(currentIdx / (MILESTONES.length - 1)) * 100}%` }} />
                      </div>
                      <div className="flex justify-between relative">
                        {MILESTONES.map((m, idx) => {
                          const Icon = m.icon;
                          const isComplete = idx < currentIdx;
                          const isCurrent = idx === currentIdx;
                          const isNext = idx === currentIdx + 1;
                          return (
                            <div key={m.id} className="flex flex-col items-center">
                              <button onClick={() => isNext && advanceMilestone(path.id, m.id)} disabled={!isNext}
                                data-testid={`milestone-${m.id}`}
                                className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all ${
                                  isComplete ? 'bg-emerald-500 text-white' :
                                  isCurrent ? 'bg-blue-500 text-white ring-3 ring-blue-500/30' :
                                  isNext ? 'bg-slate-700 text-slate-300 hover:bg-slate-600 cursor-pointer' :
                                  'bg-slate-800 text-slate-300'
                                }`}>
                                {isComplete ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                              </button>
                              <p className={`mt-1 text-xs font-medium ${isCurrent ? 'text-blue-400' : isComplete ? 'text-emerald-400' : 'text-slate-300'}`}>{m.label}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Family Agreement */}
                  <div className="border-t border-slate-700 pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-4 h-4 text-purple-400" />
                      <p className="text-white font-medium text-sm">Family Agreement</p>
                    </div>

                    {/* Student status */}
                    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 mb-2">
                      <div className="flex items-center gap-2">
                        {agreement?.student_confirmed ? <CheckCircle className="w-5 h-5 text-emerald-400" /> : <HelpCircle className="w-5 h-5 text-slate-300" />}
                        <span className="text-sm text-white">Student: {agreement?.student_confirmed ? 'Confirmed' : 'Not confirmed'}</span>
                      </div>
                      {!isParent && !agreement?.student_confirmed && (
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-7 text-xs" onClick={() => confirmPath(path.id)} data-testid="confirm-path-btn">Confirm Path</Button>
                      )}
                      {!isParent && agreement?.student_confirmed && (
                        <Button size="sm" variant="outline" onClick={() => undoConfirmation(path.id)} className="border-amber-500/40 text-amber-400 h-7 text-xs" data-testid="undo-confirm-btn"><Undo2 className="w-3 h-3 mr-1" /> Undo</Button>
                      )}
                    </div>

                    {/* Parent responses */}
                    {agreement?.parent_responses?.length > 0 ? (
                      agreement.parent_responses.map((r, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 mb-2">
                          <div className="flex items-center gap-2">
                            {r.stance === 'support' ? <ThumbsUp className="w-5 h-5 text-emerald-400" /> :
                             r.stance === 'not_approved' ? <ThumbsDown className="w-5 h-5 text-red-400" /> :
                             <HelpCircle className="w-5 h-5 text-amber-400" />}
                            <span className="text-sm text-white">Parent: <span className="capitalize">{r.stance?.replace('_',' ')}</span></span>
                          </div>
                          {isParent && (
                            <Button size="sm" variant="outline" onClick={() => undoParentResponse(path.id)} className="border-amber-500/40 text-amber-400 h-7 text-xs" data-testid="undo-parent-btn"><Undo2 className="w-3 h-3 mr-1" /> Change</Button>
                          )}
                        </div>
                      ))
                    ) : isParent && agreement?.student_confirmed ? (
                      <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <p className="text-blue-400 text-sm mb-2">Your child confirmed this path. What's your stance?</p>
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={() => parentResponse(path.id, 'support')} data-testid="parent-support-btn"><ThumbsUp className="w-3 h-3 mr-1" /> Support</Button>
                          <Button size="sm" variant="outline" className="border-amber-500 text-amber-400 h-7 text-xs" onClick={() => parentResponse(path.id, 'consider')} data-testid="parent-consider-btn"><HelpCircle className="w-3 h-3 mr-1" /> Consider</Button>
                          <Button size="sm" variant="outline" className="border-red-500 text-red-400 h-7 text-xs" onClick={() => parentResponse(path.id, 'not_approved')} data-testid="parent-not-approved-btn"><ThumbsDown className="w-3 h-3 mr-1" /> Not Approved</Button>
                        </div>
                      </div>
                    ) : isParent ? (
                      <p className="text-slate-300 text-sm p-3 bg-slate-800/30 rounded-lg text-center">Waiting for student to confirm</p>
                    ) : !agreement?.student_confirmed ? (
                      <p className="text-blue-400 text-sm p-3 bg-blue-500/10 rounded-lg text-center">Confirm this path to notify your parent/guardian</p>
                    ) : (
                      <p className="text-emerald-400 text-sm p-3 bg-emerald-500/10 rounded-lg text-center flex items-center justify-center gap-2"><CheckCircle className="w-4 h-4" /> Confirmed, waiting for parent input</p>
                    )}
                  </div>
                </GlassCard>
              );
            })}
          </div>
        ) : (
          <GlassCard className="p-8 text-center">
            <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-white font-medium mb-1">No paths selected yet</p>
            <p className="text-slate-300 text-sm">{isParent ? `${studentName} hasn't selected paths yet` : 'Use the path selector above to add your first path'}</p>
          </GlassCard>
        )}
      </div>
    </AppLayout>
  );
}
